import { notification } from "../context/NotificationsContext";
import { reportTelemetryError, reportTelemetryWarning } from "../telemetry";
import { captureScreenshot } from "../telemetry/telemetry";
import { dmnIdentity } from "../utils/dmnIdentity";
import { getEnvironmentProps, isLocalDev } from "../utils/environment";
import { consoleLog } from "../utils/nativeUtils/customActions";
import { getEndpointPath, readErrorBody } from "./responseBody";

interface ErrorResponse {
  error?: string;
  message?: string;
  payload?: {
    message?: string;
  };
  /** Field-level validation messages, e.g. `{"attachments.0._id": ["..."]}`. */
  errors?: Record<string, string[]>;
}

function isErrorResponse(obj: unknown): obj is ErrorResponse {
  return typeof obj === "object" && obj !== null;
}

const extractApiErrorMessage = (body: unknown): string => {
  if (!isErrorResponse(body)) {
    return "";
  }

  return body.payload?.message ?? body.message ?? body.error ?? "";
};

/**
 * The first field-level validation message from a 422-style response's
 * `errors` object, falling back to the response's generic `message` (e.g.
 * "The given data was invalid.") when there is no field detail to show.
 */
export const extractValidationError = (
  body: unknown
): { message: string; field?: string } => {
  const generic = extractApiErrorMessage(body);
  const firstError = isErrorResponse(body)
    ? Object.entries(body.errors ?? {})[0]
    : undefined;
  const [field, messages] = firstError ?? [undefined, undefined];

  return messages?.[0] ? { message: messages[0], field } : { message: generic };
};

const STATUS_MESSAGES: Record<number, string> = {
  400: "Bad Request",
  401: "Unauthorised",
  403: "Forbidden",
  404: "Not Found",
  405: "The HTTP method used was not allowed in this context",
  429: "Too Many Requests",
  500: "Internal Server Error",
  501: "Not Implemented"
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message: string,
    public responseError?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const getCurrentEnvironment = async (): Promise<string> => {
  try {
    const token = await dmnIdentity.getAccessToken();
    const envProps = getEnvironmentProps(token);
    return envProps?.ENVIRONMENT ?? "unknown";
  } catch (error) {
    console.warn("Could not determine environment for error reporting:", error);
    return isLocalDev() ? "development" : "unknown";
  }
};

/**
 * Display the failure toast, capture diagnostics and report the error to
 * telemetry. The body is passed in already-read so the response stream is
 * never consumed twice.
 */
const reportApiFailure = async (
  response: Response,
  errorTitle: string,
  body: unknown
): Promise<void> => {
  const apiError = extractApiErrorMessage(body);
  const message =
    STATUS_MESSAGES[response.status] ?? "An unknown error occurred";

  let heading = `${errorTitle}\n${message}`;
  if (apiError) {
    heading = `${errorTitle}\n${message}\n${apiError}`;
  }

  // Display the error message to the user
  notification({ heading, toastType: "error" });

  if (isLocalDev()) {
    consoleLog("DEBUG: ===========================");
    consoleLog("DEBUG: apiFetchNotOkHandler");
    consoleLog(`DEBUG: message: ${message}`);
    consoleLog(`DEBUG: apiError: ${apiError}`);
    consoleLog(`DEBUG: errorDetail: ${errorTitle}`);
    consoleLog(`DEBUG: error: ${JSON.stringify(body)}`);
    consoleLog(`DEBUG: response: ${JSON.stringify(response)}`);
  }

  const screenshotId = captureScreenshot(`Error: ${errorTitle}`);

  // Get environment for error reporting
  const environment = await getCurrentEnvironment();

  reportTelemetryError(
    new ApiError(
      response.status,
      response.statusText ?? "",
      `${errorTitle}: HTTP ${response.status}`,
      body
    ),
    {
      status: String(response.status),
      endpoint: getEndpointPath(response.url),
      errorTitle,
      apiError,
      message,
      releaseId: process.env.BUILD_BUILDNUMBER ?? "development",
      buildVersion: process.env.BUILD_BUILDNUMBER ?? "development",
      environment,
      ...(screenshotId && { screenshotId })
    }
  );
};

/**
 * Throws an ApiError after attempting to extract error details from the response.
 * Also displays error notifications to the user unless `silent` is set.
 *
 * @param response - The failed HTTP response object
 * @param errorTitle - A title/context for the error (e.g., "Error fetching organisations")
 * @returns Never returns - always throws an ApiError
 * @throws {ApiError} Contains HTTP status, status text, and extracted error message
 */
export const throwApiError = async (
  response: Response,
  errorTitle: string,
  options?: { silent?: boolean }
): Promise<never> => {
  const body = await readErrorBody(response);
  const apiErrorMessage = extractApiErrorMessage(body);

  // `silent` callers surface the error themselves (e.g. an inline "report
  // unavailable" panel), so skip the global error toast/telemetry/screenshot.
  if (!options?.silent) {
    await reportApiFailure(response, errorTitle, body);
  }

  const statusMessage = `HTTP ${response.status}: ${response.statusText}`;
  const fullMessage = apiErrorMessage
    ? `${statusMessage} - ${apiErrorMessage}`
    : statusMessage;

  throw new ApiError(response.status, response.statusText, fullMessage, body);
};

export const apiFetchNotOkHandler = async (
  response: Response,
  errorTitle: string
): Promise<unknown> => {
  const body = await readErrorBody(response);
  await reportApiFailure(response, errorTitle, body);
  return body;
};

/**
 * Report an expected/permission failure (e.g. a role-gated endpoint answering
 * 403) to telemetry as a warning, once per title+status+endpoint combination.
 * No toast and the body is not read — the caller keeps its own fallback.
 */
const reportedWarnings = new Set<string>();

export const reportApiWarningOnce = (
  response: Response,
  errorTitle: string
): void => {
  const endpoint = getEndpointPath(response.url);
  const status = String(response.status);
  const key = `${errorTitle}|${status}|${endpoint}`;

  if (reportedWarnings.has(key)) {
    return;
  }
  reportedWarnings.add(key);

  reportTelemetryWarning(`${errorTitle}: HTTP ${status}`, {
    status,
    endpoint,
    errorTitle
  });
};

export const errorHandler = async (err: unknown, errorTitle: string) => {
  let error = err instanceof Error ? err : undefined;
  const errorResponse = err instanceof Response ? err : undefined;

  if (errorResponse) {
    await apiFetchNotOkHandler(errorResponse, errorTitle);
    return;
  }

  // Fetch throws an Error object when it encounters a network error
  // The message property of this object will contain details about the error
  const heading = `${errorTitle}: ${error?.message ?? "An unknown error occurred"}`;

  // Display the error message to the user
  notification({ heading, toastType: "error" });

  if (isLocalDev()) {
    consoleLog("DEBUG: ===========================");
    consoleLog("DEBUG: errorHandler");
    consoleLog(`DEBUG: errorTitle: ${errorTitle}`);
    consoleLog(`DEBUG: error message: ${error?.message}`);
    consoleLog(`DEBUG: response: ${JSON.stringify(err)}`);
  }

  if (!error) {
    error = new Error(heading);
  }

  const screenshotId = captureScreenshot(`Error: ${errorTitle}`);

  // Get environment for error reporting
  const environment = await getCurrentEnvironment();

  reportTelemetryError(error, {
    errorTitle,
    heading,
    releaseId: process.env.BUILD_BUILDNUMBER ?? "development",
    buildVersion: process.env.BUILD_BUILDNUMBER ?? "development",
    environment,
    ...(screenshotId && { screenshotId })
  });
};
